// frontend/src/components/purchase-orders/PurchaseOrderActions.jsx
//
// The two places a merchant acts on a purchase order, rendering one answer —
// ./poActions.js decides which actions a PO offers; this file only renders
// that answer and dispatches. Lifecycle actions go to the caller's
// `onAction` (./usePurchaseOrderLifecycle carries them out); the files are
// handled here (./usePurchaseOrderFiles), because they need no screen state.
//
// <PurchaseOrderActions> — the PO details page, at the top right: the state's
// one primary button, then a [Download ▼] menu and a [More actions ▼] menu
// with the rest of the lifecycle — never a row of buttons. Downloads get
// their own trigger: every status offers them, so a merchant should not have
// to open a lifecycle menu to print the order. More actions renders only
// when the state has something left in it — a finished PO shows Duplicate
// (order it again) as its primary and no More menu.
//
// <PurchaseOrderRowActions> — one row of the PO list, drawn by the shared
// <GridRowActions>: the same primary as the next-step button, and one ⋯ menu
// with everything else (the files, the rest of the lifecycle). The list's grid
// renders the row as a cell, so the screen's state reaches it through
// <PurchaseOrderRowActionsScope>.
//
// Busy states: a busy file trigger keeps its label under MUI's `loading`
// spinner and goes disabled — the same affordance as a grid row's ⋯ menu
// (GridRowActions). The lifecycle actions stay live while a file
// is generating: `busy` (a lifecycle write in flight) and `downloading` are
// separate. The primary spins only while the action it names is the one
// running; any other write on the same PO merely disables it. A list row
// reacts only to writes on its own PO.
//
//   <PurchaseOrderActions
//     po={poData}                      // detail wire shape
//     mayCreateOrders={bool}           // new orders originate here: Duplicate
//     onAction={(action) => …}         // PO_ACTION.* except the files
//     busy={mutationPending}           // disables the lifecycle actions
//     running={PO_ACTION.* | null}     // the lifecycle action in flight, if any
//   />
//
//   <PurchaseOrderRowActionsScope
//     mayCreateOrders={bool}
//     onAction={(po, action) => …}     // PO_ACTION.* except the files
//     running={{ [poId]: action }}     // the writes in flight; each disables its own row only
//   >…grid…</PurchaseOrderRowActionsScope>
//   cellRenderer: (p) => <PurchaseOrderRowActions po={p.data} />

import { createContext, useContext, useMemo } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICON } from '../../icons';
import DisclosureCaret from '../common/DisclosureCaret';
import ActionMenu from '../common/ActionMenu';
import GridRowActions, { GridPrimaryButton } from '../grid/GridRowActions';
import { usePurchaseOrderFiles } from './usePurchaseOrderFiles';
import { PO_ACTION, PRIMARY_ACTIONS, purchaseOrderActions } from './poActions';

const LABEL_KEY = {
  [PO_ACTION.ISSUE]: 'purchaseOrders.actions.issue',
  [PO_ACTION.EDIT]: 'purchaseOrders.actions.edit',
  [PO_ACTION.RECEIVE]: 'purchaseOrders.actions.receive',
  [PO_ACTION.CANCEL]: 'purchaseOrders.actions.cancel',
  [PO_ACTION.CLOSE]: 'purchaseOrders.actions.close',
  [PO_ACTION.DELETE]: 'purchaseOrders.actions.delete',
  [PO_ACTION.DOWNLOAD_PDF]: 'purchaseOrders.actions.downloadPdf',
  [PO_ACTION.DOWNLOAD_CSV]: 'purchaseOrders.actions.downloadCsv',
  [PO_ACTION.DOWNLOAD_XLSX]: 'purchaseOrders.actions.downloadExcel',
  [PO_ACTION.DUPLICATE]: 'purchaseOrders.actions.duplicate',
};

const DANGER = new Set([PO_ACTION.CANCEL, PO_ACTION.CLOSE, PO_ACTION.DELETE]);

export default function PurchaseOrderActions({ po, mayCreateOrders, onAction, busy, running }) {
  const { t } = useTranslation();
  const { download, downloading } = usePurchaseOrderFiles(po);
  const { primary, downloads, more } = purchaseOrderActions(po, { mayCreateOrders });

  const downloadItems = useMemo(() => downloads.map((action) => ({
    key: action,
    label: t(LABEL_KEY[action]),
    onClick: () => download(action),
  })), [downloads, t, download]);

  const moreItems = useMemo(() => more.map((action) => ({
    key: action,
    label: t(LABEL_KEY[action]),
    danger: DANGER.has(action),
    disabled: busy,
    onClick: () => onAction(action),
  })), [more, t, busy, onAction]);

  return (
    <>
      {primary && (
        <Button variant="contained" disabled={busy} loading={running === primary} onClick={() => onAction(primary)}>
          {t(LABEL_KEY[primary])}
        </Button>
      )}
      <ActionMenu
        items={downloadItems}
        renderTrigger={(triggerProps, { open }) => (
          <Button
            variant="outlined"
            loading={downloading}
            endIcon={<DisclosureCaret open={open} size={ICON.md} />}
            {...triggerProps}
          >
            {t('purchaseOrders.actions.download')}
          </Button>
        )}
      />
      {moreItems.length > 0 && (
        <ActionMenu
          items={moreItems}
          renderTrigger={(triggerProps, { open }) => (
            <Button variant="outlined" endIcon={<DisclosureCaret open={open} size={ICON.md} />} {...triggerProps}>
              {t('purchaseOrders.actions.more')}
            </Button>
          )}
        />
      )}
    </>
  );
}

const RowActionsContext = createContext(null);

export function PurchaseOrderRowActionsScope({ mayCreateOrders, onAction, running, children }) {
  const value = useMemo(
    () => ({ mayCreateOrders, onAction, running }),
    [mayCreateOrders, onAction, running],
  );
  return <RowActionsContext.Provider value={value}>{children}</RowActionsContext.Provider>;
}

export function PurchaseOrderRowActions({ po }) {
  const { t } = useTranslation();
  const scope = useContext(RowActionsContext);
  if (!scope) throw new Error('PurchaseOrderRowActions renders inside <PurchaseOrderRowActionsScope>');
  const { mayCreateOrders, onAction, running } = scope;
  const { download, downloading } = usePurchaseOrderFiles(po);
  const { primary, downloads, more } = purchaseOrderActions(po, { mayCreateOrders });
  const runningAction = running[po.purchase_order_header_id];
  const busy = runningAction !== undefined;

  const items = [
    ...downloads.map((action) => ({
      key: action,
      label: t(LABEL_KEY[action]),
      disabled: downloading,
      onClick: () => download(action),
    })),
    ...more.map((action) => ({
      key: action,
      label: t(LABEL_KEY[action]),
      danger: DANGER.has(action),
      disabled: busy,
      onClick: () => onAction(po, action),
    })),
  ];

  return (
    <GridRowActions
      primary={primary && (
        <GridPrimaryButton
          label={t(LABEL_KEY[primary])}
          alternatives={PRIMARY_ACTIONS.map((action) => t(LABEL_KEY[action]))}
          disabled={busy}
          loading={runningAction === primary}
          onClick={() => onAction(po, primary)}
        />
      )}
      items={items}
      menuLoading={downloading}
    />
  );
}
